import { useEffect, useRef } from 'react';
import { Button } from '@/components/Elements/Button';
import { Modal } from '@/components/Elements/Modal';
import deletePopupImg from '@/assets/deletePopup.png';
import trashIcon from '@/assets/trashIcon.png';

export function ConfirmDeleteModal({
  isOpen,
  title = 'Delete Confirmation',
  description = 'This action cannot be undone and it will be permanently deleted.',
  itemName,
  illustrationSrc,
  onConfirm,
  onCancel,
  isPending = false,
  confirmText = 'Delete',
  secondaryText,
  onSecondary,
  isSecondaryPending = false,
}) {
  const busy = isPending || isSecondaryPending;
  const confirmButtonRef = useRef(null);

  // Auto-focus the delete button when the modal opens for immediate keyboard accessibility
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle Enter key globally when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        const target = e.target;
        // If user is focused on the Close button or secondary button, don't trigger delete
        if (
          target &&
          target.tagName === 'BUTTON' &&
          (target.getAttribute('aria-label') === 'Close' ||
            target.getAttribute('data-action') === 'secondary')
        ) {
          return;
        }

        e.preventDefault();
        if (!busy && onConfirm) {
          onConfirm();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, busy, onConfirm]);

  // Derive item target text to highlight in green
  let targetText = itemName;
  if (!targetText) {
    const titleLower = (title || '').toLowerCase();
    if (titleLower.includes('employee')) targetText = 'this employee';
    else if (titleLower.includes('contact')) targetText = 'this contact';
    else if (titleLower.includes('tag')) targetText = 'this tag';
    else if (
      titleLower.includes('follow-up') ||
      titleLower.includes('followup')
    )
      targetText = 'this follow-up';
    else if (titleLower.includes('task')) targetText = 'this task';
    else if (titleLower.includes('note')) targetText = 'this note';
    else if (titleLower.includes('attachment') || titleLower.includes('file'))
      targetText = 'this file';
    else if (titleLower.includes('segment')) targetText = 'this segment';
    else if (titleLower.includes('role')) targetText = 'this role';
    else if (titleLower.includes('flow')) targetText = 'this flow';
    else if (titleLower.startsWith('delete '))
      targetText = title.replace(/^delete\s+/i, '').trim();
    else targetText = 'this item';
  }

  // Ensure description does not duplicate the "Are you sure you want to delete..." heading sentence
  let cleanDescription = description || '';
  if (cleanDescription) {
    cleanDescription = cleanDescription
      .replace(
        /^Are you sure you want to (?:permanently )?delete [^.!?]*[.!?]\s*/i,
        '',
      )
      .trim();
    if (!cleanDescription) {
      cleanDescription =
        'This action cannot be undone and it will be permanently deleted.';
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!busy) onCancel();
      }}
      size="sm"
      showClose={false}
      panelClassName="p-5 sm:p-6 rounded-[24px]"
      // Above SideDrawer (z-100) so confirm never sits under a drawer
      zIndexClass="z-[110]"
    >
      <div>
        {/* Header with full-width divider — negative mx bleeds to panel edges */}
        <div className="flex items-center justify-between pb-3 border-b border-[#F0F0F0] mb-4 -mx-5 sm:-mx-6 px-5 sm:px-6">
          <h2 className="text-[20px] font-medium text-[#1F2241]">{title}</h2>
          {onCancel && (
            <button
              type="button"
              onClick={() => {
                if (!busy) onCancel();
              }}
              disabled={busy}
              className="w-8 h-8 rounded-[8px] bg-[#50AB3D] text-white flex items-center justify-center hover:opacity-90 transition-opacity focus:outline-none shrink-0 disabled:opacity-50"
              aria-label="Close"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Body content with deletePopup illustration */}
        <div className="flex flex-col items-center text-center">
          <img
            src={illustrationSrc || deletePopupImg}
            alt=""
            className="w-[160px] h-[160px] object-contain mb-3"
          />

          <h3 className="text-[20px] font-medium text-[#1F2241] leading-snug">
            Are you sure you want to delete
            <br />
            <span className="text-[#50AB3D]">{targetText}</span>?
          </h3>

          <p className="text-[14px] text-[#868686] mt-2 max-w-[300px] leading-normal font-normal">
            {cleanDescription}
          </p>

          <div className="w-full flex flex-col gap-2 mt-5">
            {onSecondary && secondaryText && (
              <Button
                variant="secondary"
                data-action="secondary"
                onClick={onSecondary}
                disabled={busy}
                className="h-11 px-6 rounded-[12px] font-semibold text-sm w-full"
              >
                <span>{isSecondaryPending ? 'Saving...' : secondaryText}</span>
              </Button>
            )}
            <Button
              ref={confirmButtonRef}
              variant="danger"
              onClick={onConfirm}
              disabled={busy}
              className="h-11 px-6 rounded-[12px] font-semibold text-sm flex items-center justify-center gap-2 w-full"
            >
              <span
                className="w-4 h-4 shrink-0 bg-white inline-block"
                style={{
                  maskImage: `url(${trashIcon})`,
                  WebkitMaskImage: `url(${trashIcon})`,
                  maskSize: 'contain',
                  WebkitMaskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  WebkitMaskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  WebkitMaskPosition: 'center',
                }}
              />
              <span>{isPending ? 'Deleting...' : confirmText}</span>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
